/**
 * Chart documentation shown behind the info icon on the Ads dashboard.
 *
 * Keys match the chart link property names in `MopaAdsComponent`.
 */
export const MopaAdsChartDocs = {
    // REVENUE

    ads_revenue_user_level: /* HTML */ `<p>
            Ad revenue recorded in the game's own events, in the selected date
            or period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - net ad revenue in the
            selected currency.
        </p>
        <p>
            <strong>What's counted:</strong> net ad revenue summed over the
            date; the currency toggle picks PLN or USD. This dashboard has no
            net/gross switch - the value is always net.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    ads_revenue_platform_report: /* HTML */ `<p>
            Ad revenue as reported by the ad platform, in the selected date or
            period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - reported ad revenue in the
            selected currency.
        </p>
        <p>
            <strong>What's counted:</strong> revenue rows from the platform
            report summed over the date. No player dimension and no validation
            filter - a 1:1 view of the report, which is what makes it comparable
            with the chart above.
        </p>
        <p>Source: <code>bi_viz.dashboard_report_ads</code>.</p>`,

    ads_running_arpu: /* HTML */ `<p>
            Cumulative ad revenue per user of a first-open cohort, by day
            number.
        </p>
        <pre>
  cohort ad revenue at that day number (numerator)
  ------------------------------
  cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since first open, day 0 being the
            install day; Y - cumulative net ad revenue per user, in the selected
            currency.
        </p>
        <p>
            The line is the running sum of those daily values, so it only
            climbs. The denominator is the day-0 cohort size for cohorts with
            any recorded activity through that day number, so churned players
            remain counted. Always net revenue.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    ads_arpdau: /* HTML */ `<p>
            Ad revenue per active user in the selected date or period.
        </p>
        <pre>
  ad revenue (numerator)
  ------------------------------
  active users (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - ad revenue per active user,
            in the selected currency.
        </p>
        <p>
            The denominator is every active player, whether or not they saw an
            ad, and comes from the activity table rather than the ad events.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (active users); valid
            players only.
        </p>`,

    ads_arpdeu: /* HTML */ `<p>
            Ad revenue per engaged user in the selected date or period.
        </p>
        <pre>
  ad revenue (numerator)
  ------------------------------
  engaged users (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - ad revenue per engaged user,
            in the selected currency.
        </p>
        <p>
            Engaged means the player completed at least one ad that day, so this
            sits at or above ARPDAU.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    // AD DELIVERY

    ads_engagement_rate: /* HTML */ `<p>
            Share of active players who completed at least one ad, in the
            selected date or period.
        </p>
        <pre>
  players with a completed ad (numerator)
  ------------------------------
  active players (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - share of active players, in
            %.
        </p>
        <p>
            Completions, not impressions: a player who starts an ad and drops it
            does not count. The two sides come from different tables.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code> (engaged),
            <code>bi_viz.dashboard_users_activity</code> (active); valid players
            only.
        </p>`,

    impressions: /* HTML */ `<p>
            Ads started in the selected date or period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - ads started.
        </p>
        <p>
            <strong>What's counted:</strong> ad show starts summed over the
            date - the moment an ad begins playing, not a finished view. How
            many of them finish is the completion rate.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players
            only.<br />
            Events: <code>AdShowStart</code>
        </p>`,

    impressions_per_deu: /* HTML */ `<p>
            Ads started per engaged user in the selected date or period.
        </p>
        <pre>
  impressions (numerator)
  ------------------------------
  engaged users (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - ads started per engaged user.
        </p>
        <p>
            The two sides cover different populations: players who never
            finished an ad are out of the denominator, but the ads they started
            still count in the numerator.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    completion_rate: /* HTML */ `<p>
            Share of started ads watched to the end, in the selected date or
            period.
        </p>
        <pre>
  completed ads (numerator)
  ------------------------------
  started ads (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - share of started ads, in %.
        </p>
        <p>A completion is counted as a granted ad reward.</p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    app_fill_rate: /* HTML */ `<p>
            Share of ad requests the network could fill, in the selected date or
            period.
        </p>
        <pre>
  requests with no ad available (numerator)
  ------------------------------
  watch clicks minus cancelled ones (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - share of requests filled, in
            %.
        </p>
        <p>
            Plotted as 1 minus that ratio, so 100% means every request was
            served. The denominator is net of cancellations, so the value can
            fall below 0 when more requests went unfilled than were left after
            cancelling.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    errors_per_ad_watch_click: /* HTML */ `<p>
            Ad loading errors per watch click in the selected date or period.
        </p>
        <pre>
  ad loading errors (numerator)
  ------------------------------
  ad watch clicks (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - errors per watch click.
        </p>
        <p>
            Cancelled clicks stay in the denominator here, unlike in App fill
            rate, so the two rates are not built on the same base.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    ecpm: /* HTML */ `<p>
            Ad revenue per thousand impressions in the selected date or period.
        </p>
        <pre>
  ad revenue x 1000 (numerator)
  ------------------------------
  impressions (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - revenue per thousand
            impressions, in the selected currency.
        </p>
        <p>
            Net revenue, currency from the toggle. Impressions are started ads,
            so this is revenue per thousand starts, not per thousand
            completions.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_ad_events</code>; valid players only.
        </p>`,

    // RECONCILIATION

    comparison_impressions: /* HTML */ `<p>
            Gap between our impression count and the ad platform's, in the
            selected date or period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - impressions, ours minus the
            platform's, so zero means agreement.
        </p>
        <p>
            <strong>What's counted:</strong> our impressions minus the ones in
            the platform report. An absolute difference, not a ratio: positive
            means the game counted more ads than the platform reported. Both
            sides are deliberately unfiltered - validating one of them would
            break the reconciliation.
        </p>
        <p>
            The game normally records around 0.5-1% fewer ads than the platform,
            so the line sits a little below zero; a change in that gap is the
            signal, not the gap itself.
        </p>
        <p>Source: <code>bi_viz.dashboard_ads_comparison</code>.</p>`,

    comparison_revenue: /* HTML */ `<p>
            Gap between our ad revenue and the ad platform's, in the selected
            date or period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - revenue in the selected
            currency, ours minus the platform's, so zero means agreement.
        </p>
        <p>
            <strong>What's counted:</strong> our revenue minus the platform
            report's, in the currency from the toggle. An absolute difference,
            not a ratio: positive means the game booked more than the platform
            reported. Both sides are deliberately unfiltered - validating one of
            them would break the reconciliation.
        </p>
        <p>
            Both figures are net, so the gap is a genuine discrepancy and not
            the network's cut; it normally stays within about 1%.
        </p>
        <p>Source: <code>bi_viz.dashboard_ads_comparison</code>.</p>`,
} as const;
